import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from 'firebase/storage';
import { ModalService } from 'npx-family-happy-common';
import { catchError, debounceTime, map, of, Subject, takeUntil } from 'rxjs';
import { UserStorageService } from 'src/app/authentication/user-storage/user-storage.service';
import { CaregiverEvaluationRecord } from 'src/app/shared/models/caregiver-evaluation-record.model';
import {
  CaregiverEvaluation,
  DEFAULT_CAREGIVER_EVALUATION,
} from 'src/app/shared/models/caregiver-evaluation.model';
import { EvaluationService } from 'src/app/shared/services/evaluation-service/evaluation.service';
import {
  NutritionistState,
  RouterStateManagerService,
} from 'src/app/shared/services/router-state-manager/router-state-manager.service';

import { ImageCropperComponent } from 'src/app/shared/components/image-cropper/image-cropper.component';
import { AlertComponent } from 'src/app/shared/components/modals/alert/alert.component';
import { DEFAULT_CAREGIVER_ENGAGEMENT } from 'src/app/shared/models/caregiver-engagement.model';
import { cellFormatter, emailFormatter } from 'src/app/shared/utils.functions';
import { Nutritionist } from 'src/app/nutritionist';
import { NutritionistService } from '../nutritionist-service/nutritionist-service.service';

@Component({
  selector: 'app-nutritionist-detail',
  templateUrl: './nutritionist-detail.component.html',
  styleUrls: ['./nutritionist-detail.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AlertComponent,
    ImageCropperComponent,
  ],
})
export class NutritionistDetailComponent implements OnInit {
  isNew = true;
  hasPayed!: boolean;
  addressSelected: boolean = false;
  showAlert = false;
  showModal = false;
  chosenPhoto!: string;
  finalPhoto!: string;
  step = 0;
  form!: UntypedFormGroup;
  nutritionist!: Nutritionist;
  nutritionistID!: string;
  generatePages: boolean = false;
  cvPages: number = 1;
  currentPage: number = 0;
  canvases: HTMLCanvasElement[] = [];
  checkNotRated = true;
  private nextTab = -1;
  private nutritionistState!: NutritionistState;
  private unsubscribe = new Subject<void>();
  private caregiverRating: CaregiverEvaluation | undefined;
  private currentUser: string = '';
  private goBackRequested = false;

  //default option for NEW NUTRITIONIST
  defaultRating = DEFAULT_CAREGIVER_EVALUATION;
  defaultEngagement = DEFAULT_CAREGIVER_ENGAGEMENT;

  constructor(
    private caregiverEvaluationService: EvaluationService,
    private nutritionistService: NutritionistService,
    private stateManager: RouterStateManagerService,
    private sanitizer: DomSanitizer,
    private modalService: ModalService,
    private activeRoute: ActivatedRoute,
    private userStorage: UserStorageService
  ) {
    this.finalPhoto = '';
  }

  ngOnInit(): void {
    this.userStorage
      .getUser()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((user) => {
        this.currentUser = user?.name ?? '';
      });
    this.form = new UntypedFormGroup({
      doc_ref: new UntypedFormControl(''),
      certificate: new UntypedFormControl(''),
      info: new UntypedFormGroup({
        name: new UntypedFormControl('', Validators.required),
        surname: new UntypedFormControl('', Validators.required),
        email: new UntypedFormControl('', Validators.required),
        phone: new UntypedFormControl('', Validators.required),
        fiscalcode: new UntypedFormControl(''),
        iban: new UntypedFormControl(''),
        age: new UntypedFormControl(0, Validators.min(0)),
        birthDate: new UntypedFormControl(''),
        resume: new UntypedFormControl(''),
        video: new UntypedFormControl(''),
        notes: new UntypedFormControl(''),
        resumeHidden: new UntypedFormControl(''),
        description: new UntypedFormControl(''),
        calendarLink: new UntypedFormControl(''),
        photo: new UntypedFormControl(''),
        stopWhatsapp: new UntypedFormControl(false),
        subscription: new UntypedFormControl(''),
        expDate: new UntypedFormControl(''),
        manualSubscription: new UntypedFormControl(''),
        expDateManual: new UntypedFormControl(''),
      }),

      lang: new UntypedFormGroup({
        title: new UntypedFormControl(''),
        english: new UntypedFormControl(''),
        italian: new UntypedFormControl(''),
        spanish: new UntypedFormControl(''),
        other1: new UntypedFormControl(''),
        other2: new UntypedFormControl(''),
        graduationType: new UntypedFormControl(''),
      }),
      updateTimestamp: new UntypedFormControl(''),
      weight_loss: new UntypedFormControl(false),
      sports_nutrition: new UntypedFormControl(false),
      pathology_management: new UntypedFormControl(false),
      gastrointestinal_conditions: new UntypedFormControl(false),
      education: new UntypedFormControl(false),
      active: new UntypedFormControl(true),
    });
    this.activeRoute.params.subscribe((param) => {
      this.nutritionistID = param.id;
    });
    try {
      this.stateManager
        .getNutritionistState()
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((state) => {
          this.nutritionistState = state;
          this.nutritionistState.id = this.nutritionistID;
          this.nutritionistState.route =
            'nutritionist-detail/' + this.nutritionistID;
          if (this.nutritionistState.step >= 0) {
            this.step = this.nutritionistState.step;
          }
          if (this.nutritionistState.nutritionist) {
            this.isNew = false;
            this.setNutritionist(this.nutritionistState.nutritionist);
            this.formInitialize(this.nutritionistState.nutritionist);
          } else if (!this.nutritionistID.match('null')) {
            const id = this.nutritionistID;
            if (id && id !== 'new') {
              this.isNew = false;
              this.nutritionistService
                .getSingleNutritionist(id)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe((nutritionists) => {
                  let nutritionist = nutritionists[0];
                  if (nutritionist) {
                    this.setNutritionist(nutritionist);
                    this.formInitialize(this.nutritionist);
                  }
                });
            }
          }
        });
    } catch (err) {
      this.modalService.showErrorMessage(
        'Errore nel recupero delle informazioni legate al nutrizionista.'
      );
    }

    this.form.valueChanges
      .pipe(debounceTime(300), takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this.nutritionist = {
          docRef: value.doc_ref?.toString().trim() || '',
          certificate: value.certificate?.toString().trim() || '',
          info: {
            name: value.info.name?.toString().trim() || '',
            surname: value.info.surname?.toString().trim() || '',
            email: value.info.email?.toString().trim() || '',
            phone: value.info.phone?.toString().trim() || '',
            fiscalcode: value.info.fiscalcode?.toString().trim() || '',
            iban: value.info.iban?.toString().trim() || '',
            age: Number(value.info.age) || 0,
            birthDate: value.info.birthDate?.toString().trim() || '',
            resume: value.info.resume?.toString().trim() || '',
            video: value.info.video?.toString().trim() || '',
            notes: value.info.notes?.toString().trim() || '',
            resumeHidden: value.info.resumeHidden?.toString().trim() || '',
            description: value.info.description?.toString().trim() || '',
            calendarLink: value.info.calendarLink?.toString().trim() || '',
            photo: value.info.photo?.toString().trim() || '',
            stopWhatsapp: Boolean(value.info.stopWhatsapp),
            subscription: value.info.subscription?.toString().trim() || '',
            expDate: value.info.expDate?.toString().trim() || '',
            manualSubscription:
              value.info.manualSubscription?.toString().trim() || '',
            expDateManual: value.info.expDateManual?.toString().trim() || '',
          },
          lang: {
            title: value.lang.title?.toString().trim() || '',
            english: value.lang.english?.toString().trim() || '',
            italian: value.lang.italian?.toString().trim() || '',
            spanish: value.lang.spanish?.toString().trim() || '',
            other1: value.lang.other1?.toString().trim() || '',
            other2: value.lang.other2?.toString().trim() || '',
            graduationType: value.lang.graduationType?.toString().trim() || '',
          },
          updateTimestamp: value.updateTimestamp?.toString().trim() || '',
          specialties: [],
          active: value.active,
        };
      });
  }

  uploadFile(_event: any, field: string) {
    try {
      this.modalService.showLoadingWithMessage('Caricamento CV in corso...');
      const file = <File>_event.target.files[0];
      const path =
        field + '/' + field + this.form.get('info.email')?.value + '.pdf';
      let storageRef = ref(getStorage(), path);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on(
        'state_changed',
        () => {},
        () => {},
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            if (field === 'info.resume') {
              this.nutritionist.info.resume = downloadURL;
            } else {
              this.nutritionist.info.resumeHidden = downloadURL;
            }
            this.form.get(field)?.setValue(downloadURL);
            if (this.isNew) {
              this.createNutritionist();
              this.modalService.hideLoading();
              this.modalService.showSuccessMessage('Caricamento riuscito.');
            } else {
              this.updateNutritionist();
              this.modalService.hideLoading();
              this.modalService.showSuccessMessage('Caricamento riuscito.');
            }
          });
        }
      );
    } catch (err) {
      this.modalService.hideLoading();
      this.modalService.showErrorMessage(
        'Errore nel caricamento del CV. Si prega di riprovare'
      );
    }
  }

  uploadImage(image: string, id: string) {
    try {
      this.modalService.showLoadingWithMessage('Caricamento foto in corso...');
      const file = this.base64ToBlob(
        image.replace('data:image/png;base64,', ''),
        'image/png'
      );
      const path = 'pictures/' + id + '.png';
      let storageRef = ref(getStorage(), path);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on(
        'state_changed',
        () => {},
        () => {},
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            this.form.get('info.photo')?.setValue(downloadURL);
            let body = this.nutritionist.info;
            body.photo = downloadURL;
            this.nutritionist.info = body;
            if (this.isNew) {
              this.createNutritionist();
              this.modalService.hideLoading();
              this.modalService.showSuccessMessage('Caricamento riuscito.');
            } else {
              this.updateNutritionist();
              this.modalService.hideLoading();
              this.modalService.showSuccessMessage('Caricamento riuscito.');
            }
          });
        }
      );
    } catch (err) {
      this.modalService.hideLoading();
      this.modalService.showErrorMessage(
        'Errore nel corso del caricamento della foto. Si prega di riprovare.'
      );
    }
    this.showModal = false;
  }

  base64ToBlob(base64Data: string, contentType: string) {
    contentType = contentType || '';
    let sliceSize = 1024;
    let byteCharacters = atob(base64Data);
    let bytesLength = byteCharacters.length;
    let slicesCount = Math.ceil(bytesLength / sliceSize);
    let byteArrays = new Array(slicesCount);

    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      let begin = sliceIndex * sliceSize;
      let end = Math.min(begin + sliceSize, bytesLength);

      let bytes = new Array(end - begin);
      for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }
    return new Blob(byteArrays, { type: contentType });
  }

  goBack() {
    if (this.form.dirty) {
      this.showAlert = true;
      this.goBackRequested = true;
    } else {
      this.nutritionistState.route = 'nutritionist-list';
      this.nutritionistState.nutritionist = undefined;
      this.nutritionistState.step = 0;
      this.nutritionistState.id = 'null';
      this.ngOnDestroy();
      this.stateManager.setNutritionistState(this.nutritionistState);
    }
  }

  formInitialize(nutritionist: Nutritionist) {
    if (nutritionist) {
      this.form.setValue({
        doc_ref: nutritionist.docRef,
        certificate: nutritionist.certificate,
        info: {
          name: nutritionist.info.name,
          surname: nutritionist.info.surname,
          email: nutritionist.info.email
            ? emailFormatter(nutritionist.info.email as string)
            : null,
          phone: nutritionist.info.phone
            ? cellFormatter(nutritionist.info.phone as string)
            : null,
          fiscalcode: nutritionist.info.fiscalcode,
          iban: nutritionist.info.iban,
          age: nutritionist.info.age,
          birthDate: nutritionist.info.birthDate,
          resume: nutritionist.info.resume,
          video: nutritionist.info.video,
          notes: nutritionist.info.notes,
          resumeHidden: nutritionist.info.resumeHidden,
          description: nutritionist.info.description,
          calendarLink: nutritionist.info.calendarLink,
          photo: nutritionist.info.photo,
          stopWhatsapp: nutritionist.info.stopWhatsapp,
          subscription: nutritionist.info.subscription,
          expDate: nutritionist.info.expDate,
          manualSubscription: nutritionist.info.manualSubscription,
          expDateManual: nutritionist.info.expDateManual,
        },
        lang: {
          title: nutritionist.lang.title,
          english: nutritionist.lang.english,
          italian: nutritionist.lang.italian,
          spanish: nutritionist.lang.spanish,
          other1: nutritionist.lang.other1,
          other2: nutritionist.lang.other2,
          graduationType: nutritionist.lang.graduationType,
        },
        updateTimestamp: nutritionist.updateTimestamp,
        weight_loss: nutritionist.specialties.includes('WEIGHT_LOSS'),
        sports_nutrition: nutritionist.specialties.includes('SPORTS_NUTRITION'),
        pathology_management: nutritionist.specialties.includes(
          'PATHOLOGY_MANAGEMENT'
        ),
        gastrointestinal_conditions: nutritionist.specialties.includes(
          'GASTROINTESTINAL_CONDITIONS'
        ),
        education: nutritionist.specialties.includes('EDUCATION'),
        active: nutritionist.active,
      });
    }
  }

  save(showMessage?: boolean) {
    if (this.form.invalid) {
      const missing = this.getMissingFields();
      this.modalService.showErrorMessage(
        'Campi obbligatori mancanti o non validi: ' + missing.join(', ')
      );
      return;
    }
    if (this.isNew) {
      this.createNutritionist();
    } else {
      this.updateNutritionist(showMessage);
    }
  }

  getMissingFields(): string[] {
    const fields: string[] = [];
    const info = this.form.get('info');
    if (info?.get('name')?.invalid) fields.push('Nome');
    if (info?.get('surname')?.invalid) fields.push('Cognome');
    if (info?.get('email')?.invalid) fields.push('Email');
    if (info?.get('phone')?.invalid) fields.push('Cellulare');
    return fields;
  }

  changeTab(tab: number) {
    if (this.form.dirty) {
      this.showAlert = true;
      this.nextTab = tab;
    } else {
      this.step = tab;
      this.nutritionistState.step = this.step;
    }
  }

  onAlertAction(save: boolean) {
    if (save && !this.isNew) {
      this.updateNutritionist();
    } else if (save && this.isNew) {
      this.createNutritionist();
    } else if (!save && this.goBackRequested) {
      if (this.nutritionist === undefined) {
        this.form.markAsPristine();
        this.form.reset();
        this.goBack();
      } else {
        this.form.markAsPristine();
        this.form.reset();
        this.formInitialize(this.nutritionist);
        this.goBack();
      }
    } else {
      this.form.markAsPristine();
      this.form.reset();
      this.formInitialize(this.nutritionist);
      this.goBack();
    }
    this.showAlert = false;
    if (this.nextTab !== -1) {
      this.step = this.nextTab;
      this.nutritionistState.step = this.step;
      this.nextTab = -1;
    }
  }

  private async createNutritionist() {
    this.nutritionist = await this.processFormData(this.form.value);
    this.nutritionistService
      .createNutritionist(this.nutritionist)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (res: any) => {
          this.modalService.showSuccessMessage('Nuovo nutrizionista creato.');
          const regex = /#(\w{2}-\d{5})/gm;
          const result = regex.exec(res.message);
          if (result) {
            this.nutritionistService
              .getSingleNutritionist(result[1])
              .pipe(takeUntil(this.unsubscribe))
              .subscribe((nutritionists) => {
                let nutritionist = nutritionists[0];
                if (nutritionist) {
                  this.isNew = !this.isNew;
                  this.nutritionist = nutritionist;
                  this.form.markAsPristine();
                  this.formInitialize(this.nutritionist);
                }
              });
          }
        },
        error: (error) => {
          if ((error.error as string).match('Key .* already exists.')) {
            this.modalService.showErrorMessage(
              "Esiste già un profilo associato all'indirizzo email fornito."
            );
          } else {
            this.modalService.showErrorMessage(
              'Si è verificato un errore nella creazione della nutritionist, si prega di riprovare.'
            );
          }
        },
      });
  }

  private async updateNutritionist(showMessage?: boolean) {
    this.nutritionist = await this.processFormData(this.form.value);
    this.nutritionistService
      .updateNutritionist(this.nutritionist)
      .pipe(
        takeUntil(this.unsubscribe),
        map(() => {
          this.publishNewCaregiverScoreUpdate();
          this.form.markAsPristine();
          this.formInitialize(this.nutritionist);
          if (showMessage) {
            this.modalService.showSuccessMessage('Nutrizionista modificato.');
          }
        }),
        catchError((error) => {
          this.modalService.showErrorMessage(
            `Si è verificato un errore nell'aggiornamento del nutrizionista.`
          );
          return of();
        })
      )
      .subscribe();
  }

  private async processFormData(data: any) {
    const processedSpecialties: string[] = [];
    if (data.weight_loss) {
      processedSpecialties.push('WEIGHT_LOSS');
    }
    if (data.sports_nutrition) {
      processedSpecialties.push('SPORTS_NUTRITION');
    }
    if (data.gastrointestinal_conditions) {
      processedSpecialties.push('GASTROINTESTINAL_CONDITIONS');
    }
    if (data.pathology_management) {
      processedSpecialties.push('PATHOLOGY_MANAGEMENT');
    }
    if (data.education) {
      processedSpecialties.push('EDUCATION');
    }

    let nutritionist: Nutritionist = {
      docRef: data.doc_ref?.toString().trim() || '',
      certificate: data.certificate?.toString().trim() || '',
      info: {
        name: data.info.name?.toString().trim() || '',
        surname: data.info.surname?.toString().trim() || '',
        email: data.info.email?.toString().trim() || '',
        phone: data.info.phone?.toString().trim() || '',
        fiscalcode: data.info.fiscalcode?.toString().trim() || '',
        iban: data.info.iban?.toString().trim() || '',
        age: Number(data.info.age) || 0,
        birthDate: data.info.birthDate?.toString().trim() || '',
        resume: data.info.resume?.toString().trim() || '',
        video: data.info.video?.toString().trim() || '',
        notes: data.info.notes?.toString().trim() || '',
        resumeHidden: data.info.resumeHidden?.toString().trim() || '',
        description: data.info.description?.toString().trim() || '',
        calendarLink: data.info.calendarLink?.toString().trim() || '',
        photo: data.info.photo?.toString().trim() || '',
        stopWhatsapp: Boolean(data.info.stopWhatsapp),
        subscription: data.info.subscription?.toString().trim() || '',
        expDate: data.info.expDate?.toString().trim() || '',
        manualSubscription:
          data.info.manualSubscription?.toString().trim() || '',
        expDateManual: data.info.expDateManual?.toString().trim() || '',
      },
      lang: {
        title: data.lang.title?.toString().trim() || '',
        english: data.lang.english?.toString().trim() || '',
        italian: data.lang.italian?.toString().trim() || '',
        spanish: data.lang.spanish?.toString().trim() || '',
        other1: data.lang.other1?.toString().trim() || '',
        other2: data.lang.other2?.toString().trim() || '',
        graduationType: data.lang.graduationType?.toString().trim() || '',
      },
      updateTimestamp: new Date().toISOString(),
      specialties: processedSpecialties,
      active: data.active,
    };
    return nutritionist;
  }

  private setNutritionist(bb: Nutritionist) {
    this.nutritionist = bb;
    this.nutritionistState.nutritionist = this.nutritionist;
    this.nutritionistState.step = this.step;
  }

  openModal(event: any) {
    let image = event.target.files[0];
    this.chosenPhoto = URL.createObjectURL(image);
    this.showModal = true;
  }

  sanitize(url: string) {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  dismiss(event: Event) {
    const elem = event.target as HTMLElement;
    if (elem === document.getElementById('modal-container')) {
      this.showModal = false;
    }
  }

  getCroppedPhoto(image: string) {
    this.finalPhoto = image;
  }

  cropperAbort() {
    let imageInput = <HTMLInputElement>(
      document.getElementById('image-selection')
    );
    imageInput.value = '';
    this.showModal = false;
  }

  htmlToCanvas(showAlert: boolean) {
    if (this.currentPage >= this.cvPages) {
      this.currentPage = 0;
      this.generatePages = false;
      return;
    }
    const element = document.getElementById('cv-page-container') as HTMLElement;
  }

  onCaregiverEvaluationChanged(newCaregiverEvaluation: CaregiverEvaluation) {
    this.caregiverRating = newCaregiverEvaluation;
    this.form.markAsTouched();
    this.form.markAsDirty();
  }

  private publishNewCaregiverScoreUpdate() {
    if (this.caregiverRating) {
      // Insert code to save new caregiver evaluation in the database
      const record: CaregiverEvaluationRecord = {
        ...this.caregiverRating,
        id: null,
        docRef: this.nutritionist.docRef,
        operator: this.currentUser,
        timestamp: new Date(),
      };
      this.caregiverRating = undefined;
      this.caregiverEvaluationService
        .addRecord(record)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.stateManager.setNutritionistState(this.nutritionistState, false);
    this.processFormData(this.form.value).then((res) => {
      this.setNutritionist(res);
    });
  }
}
